import React, { useEffect, useState } from 'react';
import { Badge } from '../ui/Badge';
import { Globe, Activity, Shield,Eye } from 'lucide-react';
import { AssetActions } from './AssetActions';
import { TechnologyStack } from './TechnologyStack';
import * as Tooltip from '@radix-ui/react-tooltip';
import { TablePagination } from '../ui/TablePagination';
import { Pagination } from '../ui/Pagination';
import { Port, PortToJSONTyped, ProtocolEnum, StateEnum } from 'api-client';
import { Link, useLocation } from 'react-router-dom';

const parseJsonData = (data: string | null) => {
  try {
    return data ? JSON.parse(data) : null;
  } catch (error) {
    return null;
  }
};
interface PaginationInfo {
  count: number;
  next: string | null;
  previous: string | null;
}

interface PortsTableProps {
  ports: Port[];
  currentPage: number;
  onPageChange: (page: number) => void;
  totalPorts: number;
  itemsPerPage: number;
  paginationInfo: PaginationInfo;
}

const parseLinkedObject = (linkedObject: any) => {
  // Handle different possible formats of linkedObject
  if (!linkedObject) return 'N/A';
                    
  // If it's a JSON string, try to parse it
  if (typeof linkedObject === 'string') {
    try {
      const parsed = JSON.parse(JSON.stringify(linkedObject));
      if (parsed && typeof parsed === 'object' && 'value' in parsed) {
        return parsed;
      }
      // If it's a simple string that's not JSON, just return it
      return linkedObject;
    } catch (e) {
      // If parsing fails, just return the string
      return linkedObject;
    }
  }
                    
  // If it's an object with a value property
  if (typeof linkedObject === 'object' && linkedObject !== null) {
    return parseJsonData(JSON.stringify(linkedObject)) ?? String(linkedObject);
  }
                    
  // Fallback
  return String(linkedObject);
};

const linkedObject: any = {
  "Subdomain": "subdomains",
  "Domain": "domains",
  "IP Address": "ip-addresses",
}

const handleViewLinkedObject = (object: any) => {
  window.open(`/${linkedObject[object.type]}/${object.id}`, '_blank')
};
export const  PortsTable: React.FC<PortsTableProps> = ({
  ports,
  currentPage,
  onPageChange,
  totalPorts,
  itemsPerPage,
  paginationInfo,
}) => {
  return (
    <div className="bg-[#0F1629] rounded-xl overflow-hidden border border-gray-800/50 flex flex-col">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-800">
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Port</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Asset</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Protocol</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">State</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Service</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Technologies</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Last Seen</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-800">
            {ports.map((port) => (
              <tr key={port.id} className="group hover:bg-gray-800/30">
                <td className="px-6 py-4">
                  <Link
                    to={`/ports/${port.id}`}
                    className="bg-[#1A1F2E] text-white"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="p-2 rounded-lg bg-gray-800/30 group-hover:bg-gray-800/50 transition-colors">
                        <Activity className="h-5 w-5 text-gray-400" />
                      </div>
                      <div className="text-sm font-medium text-white">{port.number}</div>
                    </div>
                  </Link>
                </td>
                <td className="text-sm text-gray-300 cursor-pointer" onClick={() => {
                  handleViewLinkedObject(parseLinkedObject(port.linkedObject));
                }}>
                  {/* {(() => {
                    // Handle different possible formats of linkedObject
                    if (!port.linkedObject) return 'N/A';
                    
                    // If it's a JSON string, try to parse it
                    if (typeof port.linkedObject === 'string') {
                      try {
                        const parsed = JSON.parse(JSON.stringify(port.linkedObject));
                        if (parsed && typeof parsed === 'object' && 'value' in parsed) {
                          return parsed.value;
                        }
                        // If it's a simple string that's not JSON, just return it
                        return port.linkedObject;
                      } catch (e) {
                        // If parsing fails, just return the string
                        return port.linkedObject;
                      }
                    }
                    
                    // If it's an object with a value property
                    if (typeof port.linkedObject === 'object' && port.linkedObject !== null) {
                      return parseJsonData(JSON.stringify(port.linkedObject))?.value ?? String(port.linkedObject);
                    }
                    
                    // Fallback
                    return String(port.linkedObject);
                  })()} */}
                  {parseLinkedObject(port.linkedObject)?.value}
                </td>
                <td className="text-sm text-gray-300">
                  <Badge className="bg-blue-500/10 text-blue-400 border-blue-500/20">
                    {port.protocol?.toUpperCase() || 'UNKNOWN'}
                  </Badge>
                </td>
                <td className="text-sm text-gray-300">
                  <Badge
                    className={`${port.state === StateEnum.Open ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-red-500/10 text-red-400 border-red-500/20'}`}
                  >
                    {port.state?.toUpperCase() || 'UNKNOWN'}
                  </Badge>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm text-gray-300">{port.service?.toUpperCase() || 'Unknown'}</div>
                </td>
                <td className="px-6 py-4">
                  {(() => {
                    // Handle technologies which might be a string or an array
                    if (!port.technologies) return <span className="text-sm text-gray-400">No technologies detected</span>;
                    
                    // If it's a string, try to parse it as JSON
                    if (typeof port.technologies === 'string') {
                      try {
                        const parsed = JSON.parse(port.technologies);
                        if (Array.isArray(parsed) && parsed.length > 0) {
                          return <TechnologyStack technologies={parsed} maxVisible={3} />;
                        }
                        // If it's not an array or empty, show default message
                        return <span className="text-sm text-gray-400">No technologies detected</span>;
                      } catch (e) {
                        // If parsing fails, it's not a valid JSON string
                        return <span className="text-sm text-gray-400">No technologies detected</span>;
                      }
                    }
                    
                    // If it's already an array
                    if (Array.isArray(port.technologies) && parseJsonData(port.technologies)?.length > 0) {
                      return <TechnologyStack technologies={port.technologies} maxVisible={3} />;
                    }
                    
                    // Default case
                    return <span className="text-sm text-gray-400">No technologies detected</span>;
                  })()}
                </td>
                <td className="px-6 py-4">
                  <Badge className="bg-gray-500/10 text-gray-400 border-gray-500/20">
                    {new Date(port.lastSeen).toLocaleDateString()}
                  </Badge>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2">
                    <Link
                      to={`/ports/${port.id}`}
                      className="bg-[#1A1F2E] text-white"
                    >
                      <Eye className="h-4 w-4" />
                    </Link>
                    <AssetActions assetId={String(port.id)} />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-4">
        <TablePagination
          currentPage={currentPage}
          onPageChange={onPageChange}
          totalItems={totalPorts}
          itemsPerPage={itemsPerPage}
          paginationInfo={paginationInfo}
          itemName="ports"
        />
      </div>
    </div>
  );
};
